import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react'
import {
  CreatePrescriberRequest,
  PrescriberClient,
} from '../../NewScriptPage/ScriptForm/PrescriberSlice'
import { Prescriber, PrescriberCreation } from '../../../types/prescriber'
import {
  FieldErrorsImpl,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import InputField from '../../../components/InputField'
import luhn_validate from '../../../utils/validation-helpers/LuhnValidate'
import useApi from '../../../common/utils/api/useApi'
import { mapStatusToText } from '../../../utils/status/map-status-to-text'
import { PRESCRIBER_OPRIONS } from '../../PrescriberProfilePage/constants'
import { isValidPrescriberType } from '../../../utils/validation-helpers/PrescriberValidate'
import { TOOLTIP_TEXT } from '../../../constants'
import { useEffect, useState } from 'react'
import { FORM_STAGE, PrescriberTitle } from '../constant'
import {
  isEmailLike,
  isValidEmailFormat,
} from '../../../utils/validation-helpers/emailFormatValidate'
import { isValidPrecriberNumberFormat } from '../../../utils/validation-helpers/prescriberNumberValidate'
import { RootState } from '../../../app/store'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import { trimValue } from '../../../utils/text-formatter'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../routes'
import { useSSOClientConfig } from '../../../features/sso'
import { appDidFinishLaunching } from '../../../app/appSlice'
import { QuestionIcon } from '@chakra-ui/icons'

interface ProfileCreationFormProps {
  trigger: UseFormTrigger<PrescriberCreation>
  register: UseFormRegister<PrescriberCreation>
  setValue: UseFormSetValue<PrescriberCreation>
  errors: FieldErrorsImpl<PrescriberCreation>
  getValues: UseFormGetValues<PrescriberCreation>
  setFormStage?: (stage: FORM_STAGE) => void
}

const ProfileCreationForm = ({
  trigger: triggerFormErrors,
  register,
  errors,
  setValue,
  getValues,
}: ProfileCreationFormProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [HPIICheckingStatus, setHPIICheckingStatus] = useState(getValues().hpiiStatus === 'A')
  const [disabledNext, setDisabledNext] = useState(getValues().hpiiStatus !== 'A')
  const [hasValidated, setHasValidated] = useState(!!getValues().hpiiStatus)
  const { authentication } = useAppSelector((state: RootState) => state)
  const [HPIIGroup, setHPIIGroup] = useState({
    hpii: getValues().hpii,
    family_name: getValues().familyName,
  })
  const createPrescriberStatus = useAppSelector((state) => state.prescriber.status.CreatePrescriber)
  const clientId = localStorage.getItem('client_id') || ''
  const { config: ssoClient, isPresetOrg: isClientWithPresetOrg } = useSSOClientConfig()
  const onboardingContextState = useAppSelector((state) => state.onboardingContext)
  const toast = useToast()

  if (!clientId) {
    navigate(routes.login.path)
  }

  useEffect(() => {
    if (authentication.firstName && !isEmailLike(authentication.firstName)) {
      setValue('givenNames', authentication.firstName)
    }
    if (authentication.familyName && !isEmailLike(authentication.familyName)) {
      setValue('familyName', authentication.familyName)
    }
    authentication.email && setValue('email', authentication.email)
    if (ssoClient?.forcePhoneNumber) {
      setValue('phoneNumber', ssoClient.forcePhoneNumber)
      setValue('otherPhoneNumber', ssoClient.forcePhoneNumber)
    }
  }, [authentication, setValue, ssoClient])

  const { trigger: checkHpii, loading: checkHpiiLoading } = useApi(PrescriberClient.checkHpii, {
    onSuccess: (response) => {
      setHasValidated(true)
      setValue('hpiiStatus', response.hpii_status)
      if (response.hpii_status === 'A') {
        setHPIICheckingStatus(true)
        setDisabledNext(false)
      }
      if (response.hpii_status === 'RS') {
        const clinicId = isClientWithPresetOrg
          ? ssoClient?.clinicId
          : onboardingContextState.clinicId
        setValue('hpii', response.hpii)
        checkHpii({
          hpii: response.hpii,
          family_name: getValues().familyName,
          clinicId: clinicId || '',
        })
      }
    },
    onFailure: () => {
      toast({
        title: 'Failed to validate HPI-I',
        description: 'Please check all required fields and try again',
        status: 'error',
        duration: 5000,
        position: 'top',
      })
    },
  })

  const onSubmit = async () => {
    const organizationId = isClientWithPresetOrg
      ? ssoClient?.orgId
      : onboardingContextState.organizationId
    const clinicId = isClientWithPresetOrg ? ssoClient?.clinicId : onboardingContextState.clinicId

    triggerFormErrors().then(async (isValid) => {
      if (!isValid) {
        return
      }

      const response = (await dispatch(
        PrescriberClient.CreatePrescriber({
          ...getValues(),
          clinicId,
          organizationId,
        } as CreatePrescriberRequest)
      )) as { payload?: { prescriber?: Prescriber } }

      if (response?.payload?.prescriber) {
        await dispatch(appDidFinishLaunching())
        if (isClientWithPresetOrg) {
          navigate(routes.newScript.path)
        } else {
          navigate(routes.pricePlan.path)
        }
      }
    })
  }
  const validateHpii = async () => {
    const values = getValues()
    const clinicId = isClientWithPresetOrg ? ssoClient?.clinicId : onboardingContextState.clinicId

    checkHpii({
      hpii: values.hpii,
      family_name: values.familyName,
      clinicId: clinicId || '',
    })
  }
  const onBlur = (e: React.FormEvent<HTMLInputElement>, fieldName: keyof typeof HPIIGroup) => {
    const { value } = e.target as HTMLInputElement
    if (['hpii', 'family_name'].includes(fieldName)) {
      setValue('hpiiStatus', '')
      setHasValidated(false)
    }
    if (value !== HPIIGroup?.[fieldName]) {
      setHPIICheckingStatus(false)
      setDisabledNext(true)
      setHPIIGroup({ ...HPIIGroup, [fieldName]: value })
    }
  }
  const isPhoneNumberForced = !!ssoClient?.forcePhoneNumber
  const invalidHpii = !!errors?.hpii?.message

  return (
    <>
      <SimpleGrid columns={[1, 2]} rowGap={6} columnGap={20} style={{ marginBottom: '36px' }}>
        <InputField label="Title" field="title" errors={errors} isRequired={true}>
          <Select
            placeholder="Select Title"
            {...register('title', {
              required: 'Please select a title',
            })}
          >
            {Object.values(PrescriberTitle).map((title: PrescriberTitle) => (
              <option value={title} key={title}>
                {title}
              </option>
            ))}
          </Select>
        </InputField>
        <InputField label="First Name" field="givenNames" isRequired={true} errors={errors}>
          <Input
            placeholder="First Name"
            {...register('givenNames', {
              setValueAs: trimValue,
              required: 'Required',
              maxLength: {
                value: 40,
                message: 'Prescriber name must be less than 40 Characters',
              },
              validate: (value?: string) => {
                if (!value || value.trim() === '') {
                  return 'First name cannot be empty'
                }
                return true
              },
            })}
          ></Input>
        </InputField>
        <InputField label="Family Name" field="familyName" isRequired={true} errors={errors}>
          <Input
            placeholder="Family Name"
            {...register('familyName', {
              setValueAs: trimValue,
              required: 'Required',
              maxLength: {
                value: 40,
                message: 'Name less than 40 characters',
              },
              validate: (value?: string) => {
                if (!value || value.trim() === '') {
                  return 'Family name cannot be empty'
                }
                return true
              },
              onBlur: (e) => onBlur(e, 'family_name'),
            })}
          ></Input>
        </InputField>
        <InputField label="Email" field="email" isRequired={true} errors={errors}>
          <Input
            placeholder="example@email.com"
            {...register('email', {
              setValueAs: trimValue,
              required: 'Please enter Email for confirmation emails at this prescriber',
              validate: (email?: string) => {
                if (!email || email.trim() === '') {
                  return 'Email cannot be empty'
                }
                if (email.length > 50) {
                  return 'The maximum length of an email is 50'
                }
                return (
                  isValidEmailFormat(email) ||
                  'Please enter email with the correct format, such as example@email.com'
                )
              },
            })}
          ></Input>
        </InputField>
        {/* Phone Number */}
        <InputField label="Phone Number" field="phoneNumber" errors={errors}>
          <Input
            placeholder="eg. 0411222333"
            isDisabled={isPhoneNumberForced}
            {...register('phoneNumber', {
              setValueAs: trimValue,
              validate: (value?: string) => {
                if (!value || value.trim() === '') {
                  return true
                }
                return (
                  /^(0|\+61)[0-8]\d{8}$/g.test(value) ||
                  'Please provide a valid phone number (no space or bracket)'
                )
              },
            })}
          ></Input>
          {isPhoneNumberForced && (
            <Text fontSize="sm" color="blue.600" mt={1}>
              This phone number is set by your organisation and cannot be changed.
            </Text>
          )}
        </InputField>
        <InputField
          label="Other Phone Number"
          field="otherPhoneNumber"
          errors={errors}
          useTooltipIcon
          tooltipContent={TOOLTIP_TEXT.OTHER_PHONE_NUMBER}
        >
          <Input
            placeholder="eg. 0411222333"
            isDisabled={isPhoneNumberForced}
            {...register('otherPhoneNumber', {
              setValueAs: trimValue,
              validate: (value?: string) => {
                if (!value) {
                  return true
                }
                return (
                  /^(0|\+61)[0-8]\d{8}$/g.test(value) ||
                  'Please provide a valid phone number (no space or bracket)'
                )
              },
            })}
          />
          {isPhoneNumberForced && (
            <Text fontSize="sm" color="blue.600" mt={1}>
              This phone number is set by your organisation and cannot be changed.
            </Text>
          )}
        </InputField>
        <InputField
          label="PBS Prescriber Number"
          field="prescriberNumber"
          isRequired={true}
          errors={errors}
          useTooltipIcon={true}
          tooltipContent={TOOLTIP_TEXT.PBS_PRESCRIBER_NUMBER}
        >
          <Input
            placeholder=""
            {...register('prescriberNumber', {
              setValueAs: trimValue,
              required: 'Please enter a valid prescriber number',
              validate: (prescriberNumber?: string) => {
                if (!prescriberNumber || prescriberNumber.trim() === '') {
                  return 'Prescriber number cannot be empty'
                }
                return (
                  isValidPrecriberNumberFormat(prescriberNumber) ||
                  'Please enter a valid prescriber number'
                )
              },
            })}
          />
        </InputField>
        <InputField
          label="Prescriber Type"
          field="prescriberType"
          isRequired={true}
          errors={errors}
        >
          <Select
            {...register('prescriberType', {
              setValueAs: trimValue,
              validate: (prescriberType?: string) => {
                if (!prescriberType || prescriberType.trim() === '') {
                  return 'Please select a prescriber type'
                }
                return (
                  isValidPrescriberType(String(prescriberType)) ||
                  'Please select a valid prescriber type'
                )
              },
            })}
          >
            {PRESCRIBER_OPRIONS.map(({ label, value }) => (
              <option value={value} key={label}>
                {label}
              </option>
            ))}
          </Select>
        </InputField>
        <InputField
          label="Healthcare Identifier - Individual (HPI-I)"
          field="hpii"
          isRequired={true}
          errors={errors}
          useTooltipIcon={true}
          tooltipContent={TOOLTIP_TEXT.HPI_I}
        >
          <Input
            placeholder="800361xxxxxxxxxx"
            {...register('hpii', {
              required: 'Please enter a HPI-I at this prescriber',
              setValueAs: trimValue,
              validate: (hpii?: string | number) => {
                if (!hpii || (typeof hpii === 'string' && hpii.trim() === '')) {
                  return 'HPI-I cannot be empty'
                }
                const hpiiStr = String(hpii)
                return (
                  (hpiiStr.startsWith('800361') && luhn_validate(hpiiStr, 16)) ||
                  'Please Enter a valid HPI-I Number'
                )
              },
              onBlur: (e) => onBlur(e, 'hpii'),
            })}
          />
        </InputField>
        <InputField label="Qualifications" field="qualifications" isRequired={true} errors={errors}>
          <Input
            placeholder="Please use commas to separate your qualification. e.g MBBS, DNB, FANZCA, FFPMANZCA, PG Dipl Clinical Research"
            {...register('qualifications', {
              setValueAs: trimValue,
              maxLength: { value: 50, message: 'Qualifications must be less than 50 Characters' },
              required:
                'Please enter the qualifications at this prescriber and use commas to separate your qualification',
              validate: (value?: string) => {
                if (!value || value.trim() === '') {
                  return 'Qualifications cannot be empty'
                }
                return true
              },
            })}
          />
        </InputField>
        <InputField
          label="eRx entity ID"
          field="entityId"
          errors={errors}
          useTooltipIcon={true}
          tooltipContent={TOOLTIP_TEXT.ENTITY_ID}
          isRequired={true}
        >
          <Input
            placeholder="Entity ID"
            {...register('entityId', {
              setValueAs: trimValue,
              validate: (entityId?: string) => {
                if (!entityId || entityId.trim() === '') {
                  return 'Entity ID cannot be empty'
                }
                const entityIDStr = String(entityId)
                return /^[0-9A-Z]{5}$/.test(entityIDStr) || 'Please select a valid entity ID'
              },
              required: 'Entity ID is a required field',
            })}
          />
        </InputField>

        <InputField label="HPI-I Status" field="hpiiStatus" isRequired={true} errors={errors}>
          <HStack gap={2}>
            <Alert status={HPIICheckingStatus ? 'success' : 'warning'} fontSize="md">
              {hasValidated ? <AlertIcon /> : <QuestionIcon />}
              <Text>
                {mapStatusToText[getValues().hpiiStatus] ?? (hasValidated ? 'Invalid' : '')}
              </Text>
            </Alert>
            <Button
              onClick={validateHpii}
              disabled={checkHpiiLoading || invalidHpii}
              height="100%"
              isLoading={checkHpiiLoading}
            >
              Validate
            </Button>
          </HStack>
        </InputField>
      </SimpleGrid>
      {createPrescriberStatus === 'FAILURE' && (
        <Text color="red">Create prescriber failed, Please try again later</Text>
      )}
      <Button
        style={{ marginTop: 'auto' }}
        width="200px"
        colorScheme="blue"
        onClick={onSubmit}
        disabled={disabledNext || Object.keys(errors).length > 0}
      >
        Submit
      </Button>
    </>
  )
}

export default ProfileCreationForm
